<template>
  <div>
    <!-- Announcements Modal -->
    <AnnouncementsModal
      v-model="announcementStore.showModal"
      :announcements="announcementStore.publishedAnnouncements"
    />
    <v-app-bar flat color="white" height="80">
      <!-- Left: Logo -->
      <template #prepend>
        <img
          @click="goHome"
          src="@/assets/images/layout/omd-logo.png"
          height="auto"
          alt="OMD Live"
          class="cursor-pointer"
        />
      </template>

      <!-- Right -->
      <template #append>
        <!-- GP % Dropdown -->
        <v-menu
          v-if="canSeeGp"
          v-model="gpMenuOpen"
          transition="fade-transition"
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              class="gp-pill-btn me-2"
              variant="flat"
              color="#F1F5F9"
              rounded="pill"
              @click="handleGpClick"
            >
              <div class="d-flex align-center">
                <v-icon size="18" color="#00363F" class="me-1"
                  >mdi-chart-pie</v-icon
                >
                <span class="gp-value">GP {{ totalGpPercentage }}</span>
              </div>
            </v-btn>
          </template>

          <v-card
            elevation="10"
            rounded="xl"
            min-width="280"
            class="gp-menu-card"
          >
            <div class="gp-menu-header pa-4 pb-2">
              <div
                class="text-subtitle-2 font-weight-bold"
                style="color: #64748b"
              >
                Total Growth Profit %
              </div>
              <div class="text-h5 font-weight-bold mt-1" style="color: #00363f">
                {{ totalGpPercentage }}
              </div>
            </div>

            <v-divider class="mx-4" />

            <div class="gp-menu-body pa-2">
              <div v-if="gpLoading" class="d-flex justify-center pa-6">
                <v-progress-circular indeterminate color="#00363F" size="24" />
              </div>
              <div
                v-else-if="!vendorGpData.length"
                class="text-center pa-6"
                style="color: #94a3b8"
              >
                No vendor data available
              </div>
              <div v-else class="pa-2">
                <div
                  v-for="vendor in vendorGpData"
                  :key="vendor.name"
                  class="vendor-item d-flex align-center justify-space-between px-3 py-2 mb-1"
                >
                  <div class="d-flex align-center">
                    <div class="vendor-dot me-3"></div>
                    <span
                      class="text-body-2 font-weight-medium"
                      style="color: #1e293b"
                      >{{ vendor.name }}</span
                    >
                  </div>
                  <span class="font-weight-bold" style="color: #00363f"
                    >{{ vendor.percentage }}%</span
                  >
                </div>
              </div>
            </div>

            <v-divider class="mx-4" />

            <div class="pa-4">
              <v-btn
                block
                variant="tonal"
                color="#00363F"
                rounded="lg"
                class="text-capitalize"
                to="/gp"
              >
                More Details
              </v-btn>
            </div>
          </v-card>
        </v-menu>
        <!-- Calendar / Events Dropdown -->
        <v-menu
          v-model="calendarOpen"
          transition="fade-transition"
          :close-on-content-click="false"
          @update:model-value="onCalendarToggle"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              size="small"
              variant="text"
              class="calendar-btn me-4"
            >
              <v-icon size="22" color="#00363F">mdi-calendar-month</v-icon>
            </v-btn>
          </template>

          <v-card class="cal-card" elevation="10" rounded="xl">
            <!-- Calendar Header -->
            <div class="cal-header">
              <button class="cal-nav-btn" @click="prevMonth">‹</button>
              <span class="cal-month-label">{{ currentMonthLabel }}</span>
              <button class="cal-nav-btn" @click="nextMonth">›</button>
            </div>

            <v-divider />

            <!-- Loading -->
            <div v-if="eventsLoading" class="cal-loading">
              <v-progress-circular indeterminate color="#00363F" size="24" />
            </div>

            <div v-else class="cal-body">
              <!-- Weekday Headers -->
              <div class="cal-weekdays">
                <span v-for="d in weekdays" :key="d">{{ d }}</span>
              </div>

              <!-- Days Grid -->
              <div class="cal-days">
                <button
                  v-for="day in calendarDays"
                  :key="day.key"
                  class="cal-day"
                  :class="{
                    'cal-day--muted': !day.inMonth,
                    'cal-day--today': day.isToday,
                    'cal-day--event': day.hasEvent,
                  }"
                >
                  <span>{{ day.date.getDate() }}</span>
                  <span v-if="day.hasEvent" class="cal-dot"></span>

                  <!-- Tooltip -->
                  <v-tooltip
                    v-if="day.hasEvent"
                    activator="parent"
                    location="top"
                    max-width="260"
                  >
                    <div class="cal-tooltip">
                      <div
                        v-for="(ev, idx) in day.events"
                        :key="ev.id"
                        class="cal-tooltip-event"
                        :style="{
                          borderTop:
                            idx > 0
                              ? '1px solid rgba(255,255,255,0.2)'
                              : 'none',
                          paddingTop: idx > 0 ? '8px' : '0',
                        }"
                      >
                        <div class="cal-tt-name">{{ ev.event_name }}</div>
                        <div class="cal-tt-vendor">{{ ev.vendor_name }}</div>
                        <div class="cal-tt-cost">
                          <span
                            v-for="user_name in ev.assigned_users"
                            :key="user_name.id"
                          >
                            {{ user_name.name + "," }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </v-tooltip>
                </button>
              </div>

              <!-- Bottom: events of this month summary -->
              <div v-if="currentMonthEvents.length" class="cal-events-list">
                <v-divider class="mb-2" />
                <div
                  v-for="ev in currentMonthEvents"
                  :key="ev.id"
                  class="cal-event-row"
                >
                  <div class="cal-event-dot-sm"></div>
                  <div class="cal-event-text">
                    <span class="cal-event-name">{{ ev.event_name }}</span>
                    <span class="cal-event-date">{{
                      formatEventDate(ev._day)
                    }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="cal-no-events">No events this month</div>
            </div>
          </v-card>
        </v-menu>

        <v-menu
          v-model="notifyMenuOpen"
          transition="fade-transition"
          :close-on-content-click="false"
          @update:model-value="onNotifyMenuToggle"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              size="small"
              variant="text"
              class="calendar-btn me-2"
              @click="handleNotifyClick"
            >
              <v-icon size="24" color="#00363F">mdi-trending-up</v-icon>
            </v-btn>
          </template>

          <v-card elevation="10" rounded="xl" min-width="360">
            <div class="notify-header">
              <div class="notify-nav">
                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  :disabled="!canGoOlderNotifyDay"
                  @click="goOlderNotifyDay"
                >
                  <v-icon size="18" color="#00363F">mdi-chevron-left</v-icon>
                </v-btn>

                <div class="notify-title">{{ selectedNotifyDayLabel }}</div>

                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  :disabled="!canGoNewerNotifyDay"
                  @click="goNewerNotifyDay"
                >
                  <v-icon size="18" color="#00363F">mdi-chevron-right</v-icon>
                </v-btn>
              </div>
              <v-btn
                size="small"
                variant="text"
                class="notify-refresh"
                :loading="notificationsLoading"
                @click="fetchNotifications"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <v-divider />

            <div v-if="notificationsLoading" class="notify-loading">
              <v-progress-circular indeterminate color="#00363F" size="24" />
            </div>

            <div v-else>
              <div v-if="notificationsError" class="notify-error">
                {{ notificationsError }}
              </div>

              <div v-else class="notify-body">
                <div v-if="selectedNotificationGroup" class="notify-group">
                  <!-- <div class="notify-day">
                    {{ selectedNotificationGroup.day }}
                  </div> -->
                  <div
                    v-if="!selectedNotificationGroup.items.length"
                    class="notify-empty"
                  >
                    No data available yet
                  </div>
                  <div
                    v-for="n in selectedNotificationGroup.items"
                    :key="n.id || n.created_at || n.type || JSON.stringify(n)"
                    class="notify-card"
                  >
                    <div class="notify-vendor">
                      {{ getNotifyVendorName(n) }}
                    </div>
                    <!-- <div class="notify-card-title">{{ n.title }}</div>
                    <div class="notify-card-body">{{ n.body }}</div> -->
                    <div
                      v-for="(it, idx) in getNotifyItems(n)"
                      :key="`${n.id || n.created_at}-it-${idx}`"
                      class="notify-line"
                    >
                      <span class="notify-cat">{{ it.cat_number }}</span>
                      <span class="notify-status">
                        <!-- {{ it.status }} -->
                        <img
                          v-if="it.status === 'decreased'"
                          src="@/assets/images/layout/down-grade.svg"
                          alt=""
                        />
                        <img
                          v-else
                          src="@/assets/images/layout/up-grade.svg"
                          alt=""
                        />
                      </span>
                      <span class="notify-diff">{{ it.diff }}</span>
                    </div>
                  </div>
                </div>

                <div v-else-if="!notifications.length" class="notify-empty">
                  No data available yet
                </div>
              </div>
            </div>
          </v-card>
        </v-menu>

        <!-- Announcements Icon -->
        <v-btn
          v-if="isAuthenticated"
          icon
          size="small"
          variant="text"
          class="calendar-btn me-4"
          @click="handleAnnouncementsClick"
        >
          <v-icon size="22" color="#00363F">mdi-bullhorn</v-icon>
        </v-btn>

        <!-- User Dropdown -->
        <v-menu
          transition="fade-transition"
          offset-y
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <div v-bind="props" class="user-trigger">
              <v-avatar size="40" class="me-2">
                <img width="40" height="40" :src="userImage" alt="User" />
              </v-avatar>
              <div class="text-start">
                <div class="user-name">{{ user.name }}</div>
                <div class="user-role">{{ userRole }}</div>
              </div>
              <v-icon class="ms-1" size="18" color="#9AA0A6"
                >mdi-chevron-down</v-icon
              >
            </div>
          </template>

          <v-card class="menu-card" elevation="10" rounded="xl" min-width="280">
            <!-- Header -->
            <div class="menu-header">
              <v-avatar size="44" class="me-3">
                <img width="44" height="44" :src="userImage" alt="User" />
              </v-avatar>
              <div>
                <div class="header-name">{{ user.name }}</div>
                <div class="header-email">{{ user.email }}</div>
              </div>
            </div>

            <v-divider class="my-1" />

            <!-- Actions -->
            <v-list nav density="comfortable" class="menu-list">
              <v-list-item to="/profile" rounded="lg">
                <template #prepend
                  ><v-icon>mdi-account-circle</v-icon></template
                >
                <v-list-item-title>Profile</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isAdmin" to="/users-management" rounded="lg">
                <template #prepend><v-icon>mdi-account-group</v-icon></template>
                <v-list-item-title>Users List</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isAdmin" to="/roles-management" rounded="lg">
                <template #prepend><v-icon>mdi-account-key</v-icon></template>
                <v-list-item-title>Roles List</v-list-item-title>
              </v-list-item>
            </v-list>

            <v-divider class="my-1" />

            <!-- Footer -->
            <div class="menu-footer">
              <v-btn
                variant="tonal"
                color="#00363F"
                class="w-100"
                rounded="lg"
                @click="logOut"
              >
                <v-icon start>mdi-logout</v-icon> Logout
              </v-btn>
            </div>
          </v-card>
        </v-menu>
      </template>
    </v-app-bar>
  </div>
</template>
<script>
import apiClient from "@/core/buildRequest";
import { useAuthStore } from "@/stores/auth";
import { mapState, mapActions } from "pinia";
import defaultAvatar from "@/assets/images/layout/Avatar.svg";
import AnnouncementsModal from "@/components/AnnouncementsModal.vue";
import { useAnnouncementStore } from "@/stores/announcements";
import { useSalesStore } from "@/stores/sales/index.js";
import { useGpStore } from "@/stores/gpStore/gpStore";
import { toast } from "vue3-toastify";

const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default {
  name: "TopNavbar",
  components: {
    AnnouncementsModal,
  },
  data() {
    const now = new Date();
    return {
      calendarOpen: false,
      allDays: [], // raw days from API [ { day, count, events } ]
      eventsLoading: false,
      viewDate: new Date(now.getFullYear(), now.getMonth(), 1),
      weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      notifyMenuOpen: false,
      selectedVendorForNotify: null,
      loadingNotify: false,
      apiFailMsg: "",
      notifications: [],
      notificationsLoading: false,
      notificationsError: "",
      notificationsPagination: null,
      selectedNotifyDayKey: formatDateKey(new Date()),
      notifyVendors: [
        { id: 1, label: "Allurion Tech" },
        { id: 4, label: "Bariatric Soult" },
        { id: 3, label: "Victor Medical" },
        { id: 2, label: "CM Endo Normal" },
      ],
      gpMenuOpen: false,
      gpLoading: false,
      gpStore: useGpStore(),
    };
  },
  computed: {
    currentMonthLabel() {
      return this.viewDate.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });
    },

    // Map of dateKey -> day object from API
    eventMap() {
      const map = new Map();
      this.allDays.forEach((d) => {
        if (d.count > 0) map.set(d.day, d);
      });
      return map;
    },

    // All events flattened for current view month
    currentMonthEvents() {
      const month = this.viewDate.getMonth();
      const year = this.viewDate.getFullYear();
      const result = [];
      this.allDays.forEach((d) => {
        const date = new Date(d.day);
        if (date.getMonth() === month && date.getFullYear() === year) {
          (d.events || []).forEach((ev) => result.push({ ...ev, _day: d.day }));
        }
      });
      return result;
    },

    calendarDays() {
      const year = this.viewDate.getFullYear();
      const month = this.viewDate.getMonth();
      const today = formatDateKey(new Date());

      const firstDay = new Date(year, month, 1);
      const startOffset = firstDay.getDay();
      const startDate = new Date(year, month, 1 - startOffset);

      const days = [];
      for (let i = 0; i < 42; i++) {
        const current = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate() + i,
        );
        const key = formatDateKey(current);
        const dayData = this.eventMap.get(key);
        days.push({
          key,
          date: current,
          inMonth: current.getMonth() === month,
          isToday: key === today,
          hasEvent: !!dayData,
          events: dayData ? dayData.events : [],
        });
      }
      return days;
    },
    ...mapState(useAuthStore, [
      "user",
      "userRole",
      "isAdmin",
      "isSuperAdmin",
      "isAuthenticated",
      "canSeeGp",
    ]),
    announcementStore() {
      return useAnnouncementStore();
    },
    publishedAnnouncements() {
      return this.announcementStore.publishedAnnouncements;
    },
    userImage() {
      return this.user?.image || defaultAvatar;
    },
    salesStore() {
      return useSalesStore();
    },
    selectedNotificationGroup() {
      const key = this.selectedNotifyDayKey;
      const list = Array.isArray(this.notifications) ? this.notifications : [];
      const items = list.filter((n) => this.getNotifyDayKey(n) === key);
      return {
        day: this.formatNotifyDayKey(key),
        sortKey: key,
        items,
      };
    },
    selectedNotifyDayLabel() {
      return this.formatNotifyDayKey(this.selectedNotifyDayKey);
    },
    canGoOlderNotifyDay() {
      return true;
    },
    canGoNewerNotifyDay() {
      const todayKey = formatDateKey(new Date());
      return (
        String(this.selectedNotifyDayKey).localeCompare(String(todayKey)) < 0
      );
    },
    totalGpPercentage() {
      const card = this.gpStore.gpCardsTotal.find(
        (c) => c.key === "gp_percentage",
      );
      return card ? card.value : "0";
    },
    vendorGpData() {
      if (!this.gpStore.gpData?.charts?.gp_per_supplier) return [];
      return this.gpStore.gpData.charts.gp_per_supplier.map((s) => {
        const rev = parseFloat(s.revenue) || 0;
        const cost = parseFloat(s.cost) || 0;
        const gp = rev > 0 ? ((rev - cost) / rev) * 100 : 0;

        let shortName = s.vendor_name || "";
        const n = shortName.toUpperCase();
        if (n.includes("ALLURION")) shortName = "ALLURION";
        else if (n.includes("ENDO") || n.includes("COOK"))
          shortName = "COOK ENDO";
        else if (n.includes("VICTOR")) shortName = "VICTOR";
        else if (n.includes("BARIATRIC") || n.includes("BSI"))
          shortName = "BSI";

        return {
          name: shortName,
          percentage: gp.toFixed(1),
        };
      });
    },
  },
  mounted() {
    this.fetchPublishedAnnouncements();
    this.gpStore.fetchGpData();
  },
  methods: {
    goHome() {
      this.$router.push("/home");
    },
    parseNotifyDate(dateStr) {
      if (typeof dateStr !== "string" || !dateStr) return null;

      const isoLike = dateStr.includes(" ")
        ? dateStr.replace(" ", "T")
        : dateStr;
      const d = new Date(isoLike);
      return !isNaN(d.getTime()) ? d : null;
    },
    async onCalendarToggle(isOpen) {
      if (isOpen && !this.allDays.length) {
        await this.fetchEvents();
      }
    },
    async fetchEvents() {
      this.eventsLoading = true;
      try {
        const token = localStorage.getItem("user_token");
        const res = await fetch("https://backend.omdlive.com/api/events", {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const json = await res.json();
        this.allDays = json?.data?.calendar?.days || [];
      } catch (e) {
        console.error("Failed to fetch events:", e);
        this.allDays = [];
      } finally {
        this.eventsLoading = false;
      }
    },
    prevMonth() {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth() - 1,
        1,
      );
    },
    nextMonth() {
      this.viewDate = new Date(
        this.viewDate.getFullYear(),
        this.viewDate.getMonth() + 1,
        1,
      );
    },
    formatSAR(val) {
      if (val == null) return "0 SAR";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "SAR",
        minimumFractionDigits: 0,
      }).format(val);
    },
    formatEventDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
      });
    },
    async logOut() {
      try {
        await apiClient.delete("sign-out");
      } catch (error) {
        // handled by interceptor
      } finally {
        this.clearAuth();
        this.$router.push({ path: "/login", query: { logout: 1 } });
      }
    },
    ...mapActions(useAuthStore, ["clearAuth"]),
    async fetchPublishedAnnouncements() {
      await this.announcementStore.fetchPublishedAnnouncements();
    },
    handleAnnouncementsClick() {
      if (this.isSuperAdmin) {
        this.$router.push("/announcements");
      } else {
        if (this.publishedAnnouncements.length > 0) {
          this.announcementStore.openModal();
        } else {
          toast.info("No active announcements");
        }
      }
    },
    onNotifyMenuToggle(isOpen) {
      if (isOpen) {
        this.selectedNotifyDayKey = formatDateKey(new Date());
        if (!this.notifications.length) this.fetchNotifications();
      } else {
        this.resetNotifyMenu();
      }
    },
    async fetchNotifications() {
      this.notificationsLoading = true;
      this.notificationsError = "";
      try {
        const res = await apiClient.get("notifications", { showLoader: false });
        const body = res?.data ?? {};
        const data = body?.data ?? body;
        this.notificationsPagination = data?.notifications?.pagination ?? null;
        const list = Array.isArray(data?.notifications?.data)
          ? data.notifications.data
          : Array.isArray(data?.notifications)
          ? data.notifications
          : Array.isArray(data)
          ? data
          : [];
        this.notifications = list;
      } catch (e) {
        this.notifications = [];
        this.notificationsError = "Failed to load notifications";
      } finally {
        this.notificationsLoading = false;
      }
    },
    getNotifyVendorName(n) {
      const raw = n?.data?.vendor_name ?? n?.data?.vendor?.name;
      return raw || n?.vendor_name || "";
    },
    getNotifyItems(n) {
      const items = n?.data?.items;
      return Array.isArray(items) ? items : [];
    },
    getNotifyDayKey(n) {
      const dateField = n?.date;
      const parsedFromDate = this.parseNotifyDate(dateField);
      if (parsedFromDate) {
        const y = parsedFromDate.getFullYear();
        const m = String(parsedFromDate.getMonth() + 1).padStart(2, "0");
        const day = String(parsedFromDate.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      }

      const createdAt = n?.created_at;
      if (
        typeof createdAt === "string" &&
        (createdAt.includes("منذ") || createdAt.includes("قبل"))
      ) {
        const d = new Date(); // Current time browser side

        // Match numbers if any
        const numMatch = createdAt.match(/\d+/);
        const amount = numMatch ? parseInt(numMatch[0]) : 1;

        if (createdAt.includes("دقيق") || createdAt.includes("ثان")) {
          // just minutes/seconds - keep today
        } else if (createdAt.includes("ساع")) {
          d.setHours(d.getHours() - amount);
        } else if (createdAt.includes("يوم") || createdAt.includes("أيام")) {
          d.setDate(d.getDate() - amount);
        } else if (createdAt.includes("شهر")) {
          d.setMonth(d.getMonth() - amount);
        } else if (createdAt.includes("سنة")) {
          d.setFullYear(d.getFullYear() - amount);
        }

        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      }
      const parsedFromCreatedAt = createdAt
        ? this.parseNotifyDate(createdAt)
        : null;
      if (parsedFromCreatedAt) {
        const y = parsedFromCreatedAt.getFullYear();
        const m = String(parsedFromCreatedAt.getMonth() + 1).padStart(2, "0");
        const day = String(parsedFromCreatedAt.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      }
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    formatNotifyDayKey(key) {
      const d = key ? new Date(`${key}T00:00:00`) : null;
      if (d && !isNaN(d.getTime())) {
        return d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      }
      return key || "Notifications";
    },
    goOlderNotifyDay() {
      const d = new Date(`${this.selectedNotifyDayKey}T00:00:00`);
      if (!isNaN(d.getTime())) {
        d.setDate(d.getDate() - 1);
        this.selectedNotifyDayKey = formatDateKey(d);
      }
    },
    goNewerNotifyDay() {
      if (!this.canGoNewerNotifyDay) return;
      const d = new Date(`${this.selectedNotifyDayKey}T00:00:00`);
      if (!isNaN(d.getTime())) {
        d.setDate(d.getDate() + 1);
        this.selectedNotifyDayKey = formatDateKey(d);
      }
    },
    async handleNotifyClick() {
      this.loadingNotify = true;
      this.apiFailMsg = "";
      try {
        const res = await this.salesStore.getStockCompareNotifyData();
        if (res?.key === "fail") {
          await this.fetchNotifications();
        } else if (res?.key === "success") {
          await this.fetchNotifications();
        }
      } catch (e) {
        await this.fetchNotifications();
      } finally {
        this.loadingNotify = false;
      }
    },
    async handleNotifyVendorClick(vendor) {
      this.selectedVendorForNotify = vendor;
      this.loadingNotify = true;
      this.apiFailMsg = "";
      try {
        const res = await this.salesStore.getStockCompareNotifyData();
        if (res?.key === "fail") {
          await this.fetchNotifications();
        } else if (res?.key === "success") {
          await this.fetchNotifications();
        }
      } catch (e) {
        this.apiFailMsg = "Error fetching notifications.";
        this.notifications = [];
        this.notificationsError = this.apiFailMsg;
      } finally {
        this.loadingNotify = false;
      }
    },
    resetNotifyMenu() {
      this.apiFailMsg = "";
      this.notificationsError = "";
    },
    async handleGpClick() {
      if (!this.gpStore.gpData) {
        this.gpLoading = true;
        try {
          await this.gpStore.fetchGpData();
        } catch (e) {
          console.error("Failed to fetch GP data:", e);
        } finally {
          this.gpLoading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
:deep(.v-toolbar__content) {
  box-shadow: 0px 4px 40px 0px #00000014;
  padding: 0 48px !important;
}
.gp-pill-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  color: #00363f !important;
  height: 44px !important;
  padding: 0 16px !important;
}
.gp-value {
  font-size: 14px;
}
.vendor-item {
  background: #f8fafc;
  border-radius: 10px;
  transition: background 0.2s;
}
.vendor-item:hover {
  background: #f1f5f9;
}
.vendor-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #00363f;
}
.cursor-pointer {
  cursor: pointer;
}

.notify-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
}
.notify-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.notify-title {
  font-weight: 600;
  color: #0f172a;
}
.notify-loading {
  padding: 20px 16px;
  display: flex;
  justify-content: center;
}
.notify-error {
  padding: 14px 16px;
  color: #b91c1c;
  font-size: 13px;
}
.notify-empty {
  padding: 14px 16px;
  color: #596a6d;
  font-size: 13px;
}
.notify-body {
  max-height: 420px;
  overflow: auto;
}
.notify-group {
  padding: 10px 12px;
}
.notify-day {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 6px 0 10px;
}
.notify-card {
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #ffffff;
}
.notify-vendor {
  font-size: 13px;
  font-weight: 600;
  color: #00363f;
  margin-bottom: 6px;
}
.notify-line {
  display: flex;
  gap: 10px;
  font-size: 12px;
  color: #0f172a;
  padding: 4px 0;
}
.notify-cat {
  font-weight: 600;
}
.notify-status {
  color: #64748b;
  text-transform: capitalize;
}
.notify-diff {
  font-variant-numeric: tabular-nums;
}
.notify-item-title {
  font-size: 13px;
  font-weight: 600;
}
.notify-item-subtitle {
  font-size: 12px;
  color: #596a6d;
}
.notify-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 2px;
}
.notify-card-body {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
  line-height: 1.4;
}

/* ===== User Dropdown ===== */
.user-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 12px;
  transition: background-color 0.2s ease;
}
.user-trigger:hover {
  background: #f5f7f8;
}
.user-name {
  font-weight: 600;
  line-height: 1;
  color: #333333;
  font-size: 14px;
}
.user-role {
  font-size: 12px;
  color: #596a6d;
  margin-top: 2px;
  font-weight: 400;
}
.menu-card {
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #eef0f2;
}
.menu-header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbfc 100%);
}
.header-name {
  font-weight: 700;
}
.header-email {
  font-size: 12px;
  color: #596a6d;
}
.menu-list :deep(.v-list-item) {
  padding: 10px 14px;
  margin: 2px 8px;
  border-radius: 12px;
}
.menu-list :deep(.v-list-item:hover) {
  background: #f3f6f8;
}
.menu-list :deep(.v-icon) {
  color: #00363f;
}
.menu-footer {
  padding: 12px;
}
.w-100 {
  width: 100%;
}

/* ===== Calendar Dropdown ===== */
.calendar-btn {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
}
.cal-card {
  overflow: hidden;
  border: 1px solid #eef0f2;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
  width: 320px;
}
.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbfc 100%);
}
.cal-month-label {
  font-weight: 700;
  font-size: 14px;
  color: #00363f;
}
.cal-nav-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  color: #00363f;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.cal-nav-btn:hover {
  background: #f0fafa;
}
.cal-loading {
  display: flex;
  justify-content: center;
  padding: 32px;
}
.cal-body {
  padding: 12px;
}
.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 6px;
}
.cal-weekdays span {
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}
.cal-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 4px;
}
.cal-day {
  border: none;
  background: transparent;
  padding: 0;
  outline: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: default;
}
.cal-day span:first-child {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #0f172a;
  transition: background 0.15s;
}
.cal-day--muted span:first-child {
  color: #cbd5e1;
}
.cal-day--today span:first-child {
  border: 2px solid #00363f;
  font-weight: 700;
}
.cal-day--event span:first-child {
  background: #00a896;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}
.cal-dot {
  width: 4px !important;
  height: 4px !important;
  border-radius: 50% !important;
  background: #00a896 !important;
  position: absolute;
  bottom: 2px;
}
.cal-day--event .cal-dot {
  background: rgba(255, 255, 255, 0.8) !important;
}

/* Events summary list */
.cal-events-list {
  margin-top: 8px;
  max-height: 140px;
  overflow-y: auto;
}
.cal-events-list::-webkit-scrollbar {
  width: 3px;
}
.cal-events-list::-webkit-scrollbar-thumb {
  background: #cad5e2;
  border-radius: 10px;
}
.cal-event-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 4px;
  border-radius: 6px;
  transition: background 0.12s;
}
.cal-event-row:hover {
  background: #f5fafa;
}
.cal-event-dot-sm {
  width: 6px;
  height: 6px;
  min-width: 6px;
  border-radius: 50%;
  background: #00a896;
}
.cal-event-text {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  gap: 8px;
  overflow: hidden;
}
.cal-event-name {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cal-event-date {
  font-size: 11px;
  color: #708083;
  white-space: nowrap;
  flex-shrink: 0;
}
.cal-no-events {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  padding: 12px 0 4px;
}

/* Tooltip */
.cal-tooltip {
  padding: 4px;
}
.cal-tt-name {
  font-weight: 700;
  font-size: 13px;
  color: #fff;
  margin-bottom: 2px;
}
.cal-tt-vendor {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 4px;
}
.cal-tt-cost {
  font-size: 12px;
  font-weight: 600;
  color: #5ee9b5;
}
</style>
